import { useState } from 'react';
import {
  VStack,
  HStack,
  Heading,
  ArrowCta,
  Pressable,
  Text,
} from 'components/ui';
import { Box } from '@chakra-ui/react';
import CoreDiagnosisSelectionForm from './CoreDiagnosisSelectionForm';
import { useFormData } from '../../utils/formReducer.js';
import { FORM_ACTIONS } from '../../utils/formActions.js';
import { GIConditionsList } from './GIConditionsList';
import { StickyCtaLayout } from '../ui/StickyCtaLayout';
import HeaderNav from '../HeaderNav/HeaderNav';
import { FORM_VIEW, FormViews } from './constants';
import { useDiagnosis } from 'api/onboarding/useDiagnosis';
import { DiagnosisCategory } from 'api/types';

function GiSymptomsInfo({ onNext }) {
  const { state: formData, dispatch } = useFormData();
  const [expandIbs, setExpandIbs] = useState(false);
  // Kept in the form state so coming back to this step from the next one shows
  // the answer that is already recorded.
  const hasGiCondition = formData?.hasGiCondition ?? null;
  const setHasGiCondition = (payload: boolean | null) =>
    dispatch({
      type: FORM_ACTIONS.UPDATE_FORM_FIELD,
      fieldName: 'hasGiCondition',
      payload,
    });
  const { data: coreDiagnoses } = useDiagnosis({ core: true });

  const hasSelectedIbsDiagnosis = coreDiagnoses?.some(
    (diagnosis) =>
      diagnosis.category === DiagnosisCategory.IBS &&
      formData?.diagnosis_ids.includes(diagnosis.id)
  );

  const ibsValid = !expandIbs || hasSelectedIbsDiagnosis;
  const disableCta =
    hasGiCondition && (!formData?.diagnosis_ids.length || !ibsValid);

  const selectDiagnoses = (selectedIds: string[]) => {
    dispatch({
      type: FORM_ACTIONS.UPDATE_FORM_FIELD,
      fieldName: 'diagnosis_ids',
      payload: selectedIds,
    });
  };

  const handleYes = () => {
    setHasGiCondition(true);
  };

  const handleNo = () => {
    setHasGiCondition(false);
    selectDiagnoses([]);
    setExpandIbs(false);
  };

  const renderContent = () => {
    return (
      <VStack gap={8} w="100%">
        <VStack className="header" w="100%">
          <Heading textStyle="heading.h2">
            Have you been diagnosed with a GI condition?
          </Heading>
        </VStack>

        <HStack justifyContent={'space-between'} w={'100%'} gap={3}>
          <Pressable w={'50%'} onClick={handleYes}>
            <Box
              bg={hasGiCondition === true ? 'secondary.200' : 'white'}
              px={5}
              py={'12px'}
              borderWidth={1}
              borderColor={'cement.200'}
              borderRadius={'lg'}
            >
              <Text
                textAlign={'center'}
                color={hasGiCondition === true ? 'white' : 'secondary.500'}
                fontWeight={600}
                fontSize={'md'}
                data-testid={'onboarding-gi-symptoms-yes-answer'}
              >
                Yes
              </Text>
            </Box>
          </Pressable>

          <Pressable w={'50%'} onClick={handleNo}>
            <Box
              bg={hasGiCondition === false ? 'secondary.200' : 'white'}
              px={5}
              py={'12px'}
              borderWidth={1}
              borderColor={'cement.200'}
              borderRadius={'lg'}
            >
              <Text
                textAlign={'center'}
                color={hasGiCondition === false ? 'white' : 'secondary.500'}
                fontWeight={600}
                fontSize={'md'}
                data-testid={'onboarding-gi-symptoms-no-answer'}
              >
                No
              </Text>
            </Box>
          </Pressable>
        </HStack>

        {hasGiCondition === true && (
          <>
            <CoreDiagnosisSelectionForm
              diagnoses={coreDiagnoses}
              expandIbs={expandIbs}
              selectDiagnoses={selectDiagnoses}
              setExpandIbs={setExpandIbs}
            />

            <GIConditionsList selectDiagnoses={selectDiagnoses} />
          </>
        )}
      </VStack>
    );
  };

  return (
    <StickyCtaLayout
      actionArea={
        hasGiCondition !== null && (
          <ArrowCta
            disabled={!!disableCta}
            onClick={onNext}
            data-testid={'onboarding-gi-symptoms-next-button'}
          >
            Next
          </ArrowCta>
        )
      }
      header={
        <HeaderNav
          screenName={FORM_VIEW[FormViews.questionGiCondition].trackingLabel}
          showWhatIsAybleModal
        />
      }
      maxWidth="formWidth"
      testID={'onboarding-gi-symptoms'}
    >
      {renderContent()}
    </StickyCtaLayout>
  );
}

export default GiSymptomsInfo;
